import { useMemo } from "react"
import { DateTime } from "luxon"
import { Link } from "react-router-dom"
import { Activity as ActivityIcon } from "lucide-react"
import ActivityTable from "../../activity/table"
import If from "../../if"
import ActivitySkeleton from "../../skeletons/dashboard/activity"
import { useFetchActivityFeedNewQuery } from "../../../../redux/queries"
import { useAppSelector } from "../../../hooks"
import {
  selectUserDetails,
  selectUserRoles
} from "../../../../redux/slices/auth"
import { hasUserRole } from "../../../utils"

const ITEMS_PER_PAGE = 10

const DashboardActivityTable = () => {
  const userDetails = useAppSelector(selectUserDetails)
  const userRoles = useAppSelector(selectUserRoles)

  const isBrandAdmin = hasUserRole(userRoles, "bank admin")
  const isLo = hasUserRole(userRoles, "loan officer")

  // Set up query params for last 90 days with default filters
  const queryParams = useMemo(() => {
    const now = DateTime.now()
    const params: {
      loid?: number
      org_id?: number
      limit: number
      offset: number
      timestamp_min?: number
      timestamp_max?: number
      metrics?: string
    } = {
      limit: ITEMS_PER_PAGE,
      offset: 0,
      timestamp_min: Math.floor(
        now.minus({ days: 90 }).startOf("day").toMillis() / 1000
      ),
      timestamp_max: Math.floor(now.endOf("day").toMillis() / 1000),
      metrics:
        "new_lead,new_subscription,sms_followup_sent,dashboard_initial_visit"
    }

    if (isBrandAdmin) {
      // For brand admins, use org_id to get activity from entire org
      if (userDetails?.org?.id) {
        const parsedOrgId = parseInt(userDetails.org.id, 10)
        if (!isNaN(parsedOrgId)) {
          params.org_id = parsedOrgId
        }
      }
    } else if (isLo && userDetails?.id) {
      // Only add loid for loan officers who are NOT brand admins
      const parsedLoid = parseInt(userDetails.id, 10)
      if (!isNaN(parsedLoid)) {
        params.loid = parsedLoid
      }
    }

    return params
  }, [isBrandAdmin, isLo, userDetails?.org?.id, userDetails?.id])

  const shouldSkip =
    (!isBrandAdmin && !isLo) || (!queryParams.org_id && !queryParams.loid)

  const { data: activities, isFetching } = useFetchActivityFeedNewQuery(
    queryParams,
    {
      skip: shouldSkip
    }
  )

  const data = useMemo(() => {
    return (activities ?? []).map(activity => ({
      ...activity,
      isLoActivity: false
    }))
  }, [activities])

  return (
    <If
      condition={isFetching && !activities}
      then={<ActivitySkeleton />}
      else={
        <div className="bg-white rounded-md py-4 md:pt-6 md:pb-4">
          <div className="px-4 md:px-6">
            <div className="flex items-center gap-2 mb-2">
              <ActivityIcon size={18} className="text-neutral-500" />
              <h3 className="font-normal text-base">Recent activity</h3>
              {data.length > 0 && (
                <span className="text-xs text-neutral-400 ml-auto">
                  Last 90 days
                </span>
              )}
            </div>
            <div className="max-h-[500px] overflow-y-auto overflow-x-hidden -mx-6 px-6">
              <ActivityTable
                data={data}
                isLoading={isFetching && !activities}
                hideFilters
              />
            </div>
          </div>
          <div className="flex justify-end font-medium border-t border-gray-200 pt-4 pr-4">
            <Link className="text-primary-400 text-sm" to="/recent-activity">
              View all Activities
            </Link>
          </div>
        </div>
      }
    />
  )
}

export default DashboardActivityTable
